import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Button,
  Paper,
  Typography,
  TextField,
  Tabs,
  Tab,
  IconButton,
  Divider,
} from '@mui/material';
import BackspaceIcon from '@mui/icons-material/Backspace';
import HistoryIcon from '@mui/icons-material/History';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SettingsIcon from '@mui/icons-material/Settings';
import { getAnalytics, logEvent } from 'firebase/analytics';

// TabPanel component for accessibility
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`calculator-tabpanel-${index}`}
      aria-labelledby={`calculator-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Tab accessibility properties
function a11yProps(index) {
  return {
    id: `calculator-tab-${index}`,
    'aria-controls': `calculator-tabpanel-${index}`,
  };
}

const ScientificCalculatorUI = () => {
  // State for calculator
  const [display, setDisplay] = useState('0');
  const [memory, setMemory] = useState(null);
  const [lastOperation, setLastOperation] = useState(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);
  const [history, setHistory] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [equation, setEquation] = useState('');
  
  // Accessibility states
  const [highContrastMode, setHighContrastMode] = useState(false);
  const [largeButtonsMode, setLargeButtonsMode] = useState(false);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    
    // Log tab change to Firebase Analytics
    try {
      const analytics = getAnalytics();
      logEvent(analytics, 'calculator_tab_change', {
        calculator_type: 'scientific',
        tab_index: newValue,
      });
    } catch (error) {
      console.error('Analytics error:', error);
    }
  };

  // Clear the display
  const clearDisplay = () => {
    setDisplay('0');
    setWaitingForOperand(false);
    setEquation('');
  };

  // Handle digit input
  const inputDigit = (digit) => {
    if (waitingForOperand) {
      setDisplay(String(digit));
      setWaitingForOperand(false);
      setEquation(equation + digit);
    } else {
      setDisplay(display === '0' ? String(digit) : display + digit);
      setEquation(display === '0' ? String(digit) : equation + digit);
    }
  };

  // Handle decimal point
  const inputDot = () => {
    if (waitingForOperand) {
      setDisplay('0.');
      setWaitingForOperand(false);
      setEquation(equation + '0.');
    } else if (display.indexOf('.') === -1) {
      setDisplay(display + '.');
      setEquation(equation + '.');
    }
  };

  // Handle backspace
  const handleBackspace = () => {
    if (waitingForOperand) return;
    
    if (display.length > 1) {
      setDisplay(display.substring(0, display.length - 1));
      setEquation(equation.substring(0, equation.length - 1));
    } else {
      setDisplay('0');
      setEquation(equation.substring(0, equation.length - 1));
    }
  };

  // Handle basic operations
  const handleOperation = (operation) => {
    const operationSymbol = {
      add: '+',
      subtract: '-',
      multiply: '×',
      divide: '÷',
      power: '^',
    }[operation];

    const currentValue = parseFloat(display);
    
    if (memory === null) {
      setMemory(currentValue);
    } else if (lastOperation) {
      const result = performOperation(memory, currentValue, lastOperation);
      setMemory(result);
      setDisplay(String(result));
      
      // Add to history
      setHistory([...history, { equation: equation, result: result }]);
    }

    setWaitingForOperand(true);
    setLastOperation(operation);
    setEquation(equation + ' ' + operationSymbol + ' ');
  };

  // Perform the actual operation
  const performOperation = (a, b, operation) => {
    switch(operation) {
      case 'add': return a + b;
      case 'subtract': return a - b;
      case 'multiply': return a * b;
      case 'divide': return a / b;
      case 'power': return Math.pow(a, b);
      default: return b;
    }
  };

  // Handle equals
  const handleEquals = () => {
    const currentValue = parseFloat(display);
    
    if (memory === null) {
      return;
    }
    
    if (lastOperation) {
      const result = performOperation(memory, currentValue, lastOperation);
      setDisplay(String(result));
      setMemory(null);
      setLastOperation(null);
      setWaitingForOperand(true);
      
      // Add to history
      const fullEquation = equation + ' = ' + result;
      setHistory([...history, { equation: fullEquation, result: result }]);
      setEquation('');
      
      // Log calculation to Firebase Analytics
      try {
        const analytics = getAnalytics();
        logEvent(analytics, 'calculator_calculation', {
          calculator_type: 'scientific',
          operation_type: lastOperation,
        });
      } catch (error) {
        console.error('Analytics error:', error);
      }
    }
  };

  // Handle scientific functions
  const handleScientificFunction = (func) => {
    const currentValue = parseFloat(display);
    let result;
    
    switch(func) {
      case 'sin':
        result = Math.sin(currentValue);
        break;
      case 'cos':
        result = Math.cos(currentValue);
        break;
      case 'tan':
        result = Math.tan(currentValue);
        break;
      case 'log':
        result = Math.log10(currentValue);
        break;
      case 'ln':
        result = Math.log(currentValue);
        break;
      case 'sqrt':
        result = Math.sqrt(currentValue);
        break;
      case 'square':
        result = currentValue * currentValue;
        break;
      case 'factorial':
        result = factorial(currentValue);
        break;
      case 'pi':
        result = Math.PI;
        break;
      case 'e':
        result = Math.E;
        break;
      default:
        return;
    }
    
    setDisplay(String(result));
    setWaitingForOperand(true);
    
    // Add to history
    const funcEquation = `${func}(${currentValue}) = ${result}`;
    setHistory([...history, { equation: funcEquation, result: result }]);
    
    // Log scientific function to Firebase Analytics
    try {
      const analytics = getAnalytics();
      logEvent(analytics, 'calculator_scientific_function', {
        calculator_type: 'scientific',
        function_type: func,
      });
    } catch (error) {
      console.error('Analytics error:', error);
    }
  };

  // Calculate factorial
  const factorial = (n) => {
    if (n < 0) return NaN;
    if (n <= 1) return 1;
    let result = 1;
    for (let i = 2; i <= n; i++) {
      result *= i;
    }
    return result;
  };

  // Handle keyboard input
  useEffect(() => {
    const handleKeyDown = (event) => {
      const { key } = event;
      
      if (key === 'Escape') {
        event.preventDefault();
        clearDisplay();
      } else if (key === 'Backspace') {
        event.preventDefault();
        handleBackspace();
      } else if (key === 'Enter' || key === '=') {
        event.preventDefault();
        handleEquals();
      } else if (key === '+') {
        event.preventDefault();
        handleOperation('add');
      } else if (key === '-') {
        event.preventDefault();
        handleOperation('subtract');
      } else if (key === '*') {
        event.preventDefault();
        handleOperation('multiply');
      } else if (key === '/') {
        event.preventDefault();
        handleOperation('divide');
      } else if (key === '^') {
        event.preventDefault();
        handleOperation('power');
      } else if (key === '.') {
        event.preventDefault();
        inputDot();
      } else if (/\d/.test(key)) {
        event.preventDefault();
        inputDigit(parseInt(key, 10));
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [display, waitingForOperand, memory, lastOperation, equation]);

  // Button styles based on accessibility settings
  const getButtonStyle = (variant = 'contained') => ({
    fontSize: largeButtonsMode ? '1.2rem' : '1rem',
    padding: largeButtonsMode ? '12px' : '8px',
    margin: '4px',
    bgcolor: highContrastMode ? 'black' : undefined,
    color: highContrastMode ? 'white' : undefined,
    '&:hover': {
      bgcolor: highContrastMode ? 'rgba(255, 255, 255, 0.2)' : undefined,
    },
    variant,
  });

  return (
    <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto' }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 2, 
          bgcolor: highContrastMode ? 'black' : 'background.paper',
          color: highContrastMode ? 'white' : 'text.primary',
        }}
      >
        {/* Calculator Display */}
        <TextField
          fullWidth
          variant="outlined"
          value={display}
          InputProps={{
            readOnly: true,
            sx: { 
              textAlign: 'right', 
              fontSize: '1.5rem',
              fontWeight: 'bold',
              bgcolor: highContrastMode ? '#333' : 'background.paper',
              color: highContrastMode ? 'white' : 'text.primary',
            },
          }}
          aria-label="Calculator display"
          sx={{ mb: 2 }}
        />
        
        {/* Equation Display */}
        <Typography 
          variant="body2" 
          sx={{ 
            textAlign: 'right', 
            mb: 1, 
            height: '1.5rem',
            color: highContrastMode ? '#ccc' : 'text.secondary',
          }}
        >
          {equation}
        </Typography>

        {/* Calculator Tabs */}
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="Calculator tabs"
          sx={{ 
            mb: 2,
            '& .MuiTab-root': {
              color: highContrastMode ? 'white' : undefined,
            },
            '& .Mui-selected': {
              color: highContrastMode ? '#90caf9' : undefined,
            },
          }}
        >
          <Tab 
            icon={<Typography variant="body2">Standard</Typography>} 
            {...a11yProps(0)} 
            aria-label="Standard calculator"
          />
          <Tab 
            icon={<ShowChartIcon />} 
            {...a11yProps(1)} 
            aria-label="Graphing calculator"
          />
          <Tab 
            icon={<HistoryIcon />} 
            {...a11yProps(2)} 
            aria-label="Calculator history"
          />
          <Tab 
            icon={<SettingsIcon />} 
            {...a11yProps(3)} 
            aria-label="Accessibility settings"
          />
        </Tabs>

        {/* Standard Calculator Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={1}>
            {/* Scientific Functions Row 1 */}
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={3}>
                  <Button 
                    fullWidth 
                    onClick={() => handleScientificFunction('sin')}
                    sx={getButtonStyle()}
                    aria-label="Sine function"
                  >
                    sin
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button 
                    fullWidth 
                    onClick={() => handleScientificFunction('cos')}
                    sx={getButtonStyle()}
                    aria-label="Cosine function"
                  >
                    cos
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button 
                    fullWidth 
                    onClick={() => handleScientificFunction('tan')}
                    sx={getButtonStyle()}
                    aria-label="Tangent function"
                  >
                    tan
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button 
                    fullWidth 
                    onClick={() => handleScientificFunction('log')}
                    sx={getButtonStyle()}
                    aria-label="Logarithm base 10"
                  >
                    log
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            {/* Scientific Functions Row 2 */}
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={3}>
                  <Button 
                    fullWidth 
                    onClick={() => handleScientificFunction('ln')}
                    sx={getButtonStyle()}
                    aria-label="Natural logarithm"
                  >
                    ln
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button 
                    fullWidth 
                    onClick={() => handleScientificFunction('sqrt')}
                    sx={getButtonStyle()}
                    aria-label="Square root"
                  >
                    √
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button 
                    fullWidth 
                    onClick={() => handleScientificFunction('square')}
                    sx={getButtonStyle()}
                    aria-label="Square"
                  >
                    x²
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button 
                    fullWidth 
                    onClick={() => handleOperation('power')}
                    sx={getButtonStyle()}
                    aria-label="Power"
                  >
                    x^y
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            {/* Scientific Functions Row 3 */}
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={3}>
                  <Button 
                    fullWidth 
                    onClick={() => handleScientificFunction('factorial')}
                    sx={getButtonStyle()}
                    aria-label="Factorial"
                  >
                    n!
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button 
                    fullWidth 
                    onClick={() => handleScientificFunction('pi')}
                    sx={getButtonStyle()}
                    aria-label="Pi constant"
                  >
                    π
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button 
                    fullWidth 
                    onClick={() => handleScientificFunction('e')}
                    sx={getButtonStyle()}
                    aria-label="Euler's number"
                  >
                    e
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button 
                    fullWidth 
                    color="error"
                    onClick={clearDisplay}
                    sx={getButtonStyle()}
                    aria-label="Clear"
                  >
                    C
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            {/* Basic Calculator Grid */}
            {/* Row 1 */}
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={3}>
                  <Button 
                    fullWidth 
                    onClick={() => inputDigit(7)}
                    sx={getButtonStyle()}
                    aria-label="Seven"
                  >
                    7
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button 
                    fullWidth 
                    onClick={() => inputDigit(8)}
                    sx={getButtonStyle()}
                    aria-label="Eight"
                  >
                    8
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button 
                    fullWidth 
                    onClick={() => inputDigit(9)}
                    sx={getButtonStyle()}
                    aria-label="Nine"
                  >
                    9
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button 
                    fullWidth 
                    color="primary"
                    onClick={() => handleOperation('divide')}
                    sx={getButtonStyle()}
                    aria-label="Divide"
                  >
                    ÷
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            {/* Row 2 */}
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={3}>
                  <Button 
                    fullWidth 
                    onClick={() => inputDigit(4)}
                    sx={getButtonStyle()}
                    aria-label="Four"
                  >
                    4
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button 
                    fullWidth 
                    onClick={() => inputDigit(5)}
                    sx={getButtonStyle()}
                    aria-label="Five"
                  >
                    5
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button 
                    fullWidth 
                    onClick={() => inputDigit(6)}
                    sx={getButtonStyle()}
                    aria-label="Six"
                  >
                    6
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button 
                    fullWidth 
                    color="primary"
                    onClick={() => handleOperation('multiply')}
                    sx={getButtonStyle()}
                    aria-label="Multiply"
                  >
                    ×
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            {/* Row 3 */}
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={3}>
                  <Button 
                    fullWidth 
                    onClick={() => inputDigit(1)}
                    sx={getButtonStyle()}
                    aria-label="One"
                  >
                    1
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button 
                    fullWidth 
                    onClick={() => inputDigit(2)}
                    sx={getButtonStyle()}
                    aria-label="Two"
                  >
                    2
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button 
                    fullWidth 
                    onClick={() => inputDigit(3)}
                    sx={getButtonStyle()}
                    aria-label="Three"
                  >
                    3
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button 
                    fullWidth 
                    color="primary"
                    onClick={() => handleOperation('subtract')}
                    sx={getButtonStyle()}
                    aria-label="Subtract"
                  >
                    -
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            {/* Row 4 */}
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={3}>
                  <Button 
                    fullWidth 
                    onClick={() => inputDigit(0)}
                    sx={getButtonStyle()}
                    aria-label="Zero"
                  >
                    0
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button 
                    fullWidth 
                    onClick={inputDot}
                    sx={getButtonStyle()}
                    aria-label="Decimal point"
                  >
                    .
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <IconButton 
                    fullWidth 
                    onClick={handleBackspace}
                    sx={{
                      ...getButtonStyle('outlined'),
                      borderRadius: '4px',
                    }}
                    aria-label="Backspace"
                  >
                    <BackspaceIcon />
                  </IconButton>
                </Grid>
                <Grid item xs={3}>
                  <Button 
                    fullWidth 
                    color="primary"
                    onClick={() => handleOperation('add')}
                    sx={getButtonStyle()}
                    aria-label="Add"
                  >
                    +
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            {/* Equals Row */}
            <Grid item xs={12}>
              <Button 
                fullWidth 
                color="secondary"
                onClick={handleEquals}
                sx={{
                  ...getButtonStyle(),
                  mt: 1,
                  py: 1.5,
                  fontSize: largeButtonsMode ? '1.4rem' : '1.2rem',
                }}
                aria-label="Equals"
              >
                =
              </Button>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Graphing Calculator Tab - Placeholder */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="body1" align="center" sx={{ my: 4 }}>
            Graphing functionality will be implemented in the next phase.
          </Typography>
        </TabPanel>

        {/* History Tab - Placeholder */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="body1" align="center" sx={{ my: 4 }}>
            History functionality will be implemented in the next phase.
          </Typography>
        </TabPanel>

        {/* Accessibility Settings Tab - Placeholder */}
        <TabPanel value={tabValue} index={3}>
          <Typography variant="body1" align="center" sx={{ my: 4 }}>
            Accessibility settings will be implemented in the next phase.
          </Typography>
        </TabPanel>

        {/* Keyboard Shortcuts Info */}
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" display="block" gutterBottom>
            <strong>Keyboard Shortcuts:</strong> Use number keys for digits, operators (+, -, *, /), Enter for equals, Backspace to delete, and Escape to clear.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default ScientificCalculatorUI;
